import {
  Autocomplete,
  AutocompleteRenderValueGetItemProps,
  Divider,
  FilterOptionsState,
  FormLabel,
  IconButton,
  ListItem,
  ListItemIcon,
  MenuItem,
  Popover,
  TextField,
  Typography,
  darken,
} from '@mui/material';
import { MoreVertical, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as S from './styles';
import { dropdownPaperSx } from './styles';
import { TagOption } from './types';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Available palette hex colors shown in the edit panel. */
const TAG_COLORS = [
  '#ffffff',
  '#CCD8DA',
  '#FF9898',
  '#B9A8EB',
  '#A6C0DC',
  '#B6E3EA',
  '#AACB8F',
  '#FDDC75',
  '#F7B273',
];

/** Default hex color for newly created tags. */
const DEFAULT_TAG_COLOR = '#DBF1F4';

/** Sentinel id for the synthetic "Create new" dropdown entry. */
const CREATE_OPTION_ID = '__TAG_SELECT_CREATE__';

/** Debounce for persisting a renamed tag while the user types (ms). */
const RENAME_DEBOUNCE_MS = 400;

/** Width reserved for the edit submenu when deciding which side it opens on. */
const SUBMENU_WIDTH = 210;

/** Gap between the main menu and the edit submenu (px). */
const SUBMENU_GAP = 8;

// ─── Types ──────────────────────────────────────────────────────────────────

interface EditState {
  /** The tag being edited, or null when the submenu is closed. */
  option: TagOption | null;
  name: string;
  color: string;
  error?: string | null;
  isDeleting?: boolean;
}

const CLOSED_EDIT_STATE: EditState = {
  option: null,
  name: '',
  color: DEFAULT_TAG_COLOR,
  error: null,
  isDeleting: false,
};

export interface TagSelectProps {
  value: TagOption[];
  options: TagOption[];
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  onChange: (tags: TagOption[]) => void;
  onCreateTag?: (name: string, color: string) => void;
  onEditTag?: (updated: TagOption) => Promise<void> | void;
  onDeleteTag?: (id: string) => Promise<void> | void;
}

/**
 * TagSelect — multi-select chip input built on MUI Autocomplete.
 *
 * - Focusing the field opens the dropdown; typing filters the options.
 * - Each option is a colored chip; hovering reveals a ⋯ button.
 * - Clicking ⋯ opens an edit submenu (MUI Popover) to rename / recolor / delete.
 * - "Create new <name>" appears when the typed text matches no existing option.
 *
 * The submenu is a Popover (not a hand-rolled portal) so focus management and
 * click-outside handling come for free — clicking the name field types into it,
 * clicking anywhere outside closes just the submenu.
 */
export default function TagSelect({
  value,
  options,
  placeholder,
  label,
  disabled = false,
  onChange,
  onCreateTag,
  onEditTag,
  onDeleteTag,
}: TagSelectProps) {
  const { t } = useTranslation('dashboard', { keyPrefix: 'global' });
  const instanceId = useId();

  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [edit, setEdit] = useState<EditState>(CLOSED_EDIT_STATE);
  // The dropdown's paper element — the submenu anchors to it (not the ⋯ button)
  // so it sits beside the whole menu and flips left/right by available space.
  const [menuEl, setMenuEl] = useState<HTMLDivElement | null>(null);

  const renameTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isEditOpen = edit.option !== null && menuEl !== null;

  const clearRenameTimer = useCallback(() => {
    if (renameTimer.current) {
      clearTimeout(renameTimer.current);
      renameTimer.current = null;
    }
  }, []);

  useEffect(() => clearRenameTimer, [clearRenameTimer]);

  // ─── Edit submenu ──────────────────────────────────────────────────────────

  const openEdit = useCallback((option: TagOption) => {
    setEdit({
      option,
      name: option.name,
      color: option.color,
      error: null,
      isDeleting: false,
    });
  }, []);

  const closeEdit = useCallback(() => {
    clearRenameTimer();
    setEdit(CLOSED_EDIT_STATE);
  }, [clearRenameTimer]);

  const handleNameChange = useCallback(
    (name: string) => {
      setEdit((s) => ({ ...s, name, error: null }));
      const target = edit.option;
      if (!onEditTag || !target) return;

      clearRenameTimer();
      renameTimer.current = setTimeout(() => {
        renameTimer.current = null;
        const trimmed = name.trim();
        if (!trimmed || trimmed === target.name) return;
        void (async () => {
          try {
            await onEditTag({ ...target, name: trimmed });
          } catch (err) {
            setEdit((s) => ({
              ...s,
              error: resolveError(err) || t('edit_failed', 'Update failed'),
            }));
          }
        })();
      }, RENAME_DEBOUNCE_MS);
    },
    [edit.option, onEditTag, clearRenameTimer, t],
  );

  const handleColorChange = useCallback(
    (color: string) => {
      setEdit((s) => ({ ...s, color }));
      if (onEditTag && edit.option) {
        void onEditTag({ ...edit.option, color });
      }
    },
    [edit.option, onEditTag],
  );

  const handleDelete = useCallback(async () => {
    const target = edit.option;
    if (!target) return;
    setEdit((s) => ({ ...s, isDeleting: true, error: null }));
    try {
      if (onDeleteTag) await onDeleteTag(target.id);
      onChange(value.filter((v) => v.id !== target.id));
      closeEdit();
    } catch (err) {
      setEdit((s) => ({
        ...s,
        isDeleting: false,
        error: resolveError(err) || t('delete_failed', 'Delete failed'),
      }));
    }
  }, [edit.option, onDeleteTag, onChange, value, closeEdit, t]);

  // ─── Create new ──────────────────────────────────────────────────────────

  const handleCreateNew = useCallback(() => {
    const trimmed = inputValue.trim();
    if (onCreateTag && trimmed) {
      onCreateTag(trimmed, DEFAULT_TAG_COLOR);
      setInputValue('');
    }
  }, [inputValue, onCreateTag]);

  // ─── Autocomplete callbacks ────────────────────────────────────────────────

  const handleChange = useCallback(
    (_e: React.SyntheticEvent, next: (string | TagOption)[]) => {
      onChange(
        next.filter((v): v is TagOption => typeof v === 'object' && v.id !== CREATE_OPTION_ID),
      );
    },
    [onChange],
  );

  const filterOptions = useCallback(
    (opts: TagOption[], state: FilterOptionsState<TagOption>): TagOption[] => {
      const q = state.inputValue.trim().toLowerCase();
      const filtered = q ? opts.filter((o) => o.name.toLowerCase().includes(q)) : opts.slice();

      // Offer "Create new" only when the text matches no existing option exactly.
      const hasExactMatch = opts.some((o) => o.name.toLowerCase() === q);
      if (q && onCreateTag && !hasExactMatch) {
        filtered.push({
          id: CREATE_OPTION_ID,
          name: state.inputValue.trim(),
          color: DEFAULT_TAG_COLOR,
        });
      }
      return filtered;
    },
    [onCreateTag],
  );

  // ─── Renderers ─────────────────────────────────────────────────────────────

  const renderValue = useCallback(
    (items: TagOption[], getItemProps: AutocompleteRenderValueGetItemProps<true>) =>
      items.map((option, index) => {
        const { key, onDelete, ...itemProps } = getItemProps({ index });
        return (
          <S.StyledChip
            key={key}
            {...itemProps}
            // Show the delete (×) button only while the field is focused; chips
            // sit clean at rest. preventDefault on the icon's mousedown keeps the
            // input focused so the click isn't swallowed by the chip unmounting
            // its own delete button mid-click.
            onMouseDown={focused ? (e: React.MouseEvent) => e.preventDefault() : undefined}
            onDelete={focused ? onDelete : undefined}
            label={option.name}
            tagColor={option.color}
          />
        );
      }),
    [focused],
  );

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: TagOption) => {
      const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };

      if (option.id === CREATE_OPTION_ID) {
        return (
          <ListItem key={key} {...rest} onClick={handleCreateNew}>
            <Typography variant='caption' color='text.secondary' mr={1} fontWeight={700}>
              {t('create_new')}
            </Typography>
            <S.StyledChip label={option.name} tagColor={DEFAULT_TAG_COLOR} />
          </ListItem>
        );
      }

      // Selected rows are highlighted by MUI's `aria-selected` / `.Mui-selected`
      // styling (see the MuiMenuItem theme override) — no checkmark, per design.
      return (
        <ListItem key={key} {...rest}>
          <S.OptionRow
            sx={{
              '&:hover .MuiChip-root': {
                boxShadow: `inset 0 0 0 1px ${darken(option.color, 0.2)}`,
              },
            }}
          >
            <S.StyledChip label={option.name} tagColor={option.color} />
            {onEditTag && (
              <S.OptionActions>
                <IconButton
                  size='small'
                  className='option-action'
                  aria-label={`Edit tag ${option.name}`}
                  // Keep the input focused (and the dropdown open) when opening
                  // the submenu — without this, mousedown blurs the field.
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(option);
                  }}
                  sx={{ color: 'text.secondary', padding: '2px', borderRadius: '4px' }}
                >
                  <MoreVertical size={14} />
                </IconButton>
              </S.OptionActions>
            )}
          </S.OptionRow>
        </ListItem>
      );
    },
    [handleCreateNew, openEdit, onEditTag, t],
  );

  // ─── Render ──────────────────────────────────────────────────────────────

  // Open the submenu on whichever side of the menu has room; default to the
  // right and only flip left when the right edge is too close to the viewport.
  // Position it explicitly (anchorPosition) so the 8px gap is exact — margin on
  // the Popover paper gets swallowed by MUI's own positioning.
  const menuRect = menuEl?.getBoundingClientRect() ?? null;
  const placeRight = !menuRect || window.innerWidth - menuRect.right >= SUBMENU_WIDTH + SUBMENU_GAP;
  const submenuAnchor = menuRect
    ? {
        top: menuRect.top,
        left: placeRight ? menuRect.right + SUBMENU_GAP : menuRect.left - SUBMENU_GAP,
      }
    : undefined;

  return (
    <S.AutocompleteWrapper>
      <Autocomplete<TagOption, true, false, false>
        id={`tag-select-${instanceId}`}
        multiple
        openOnFocus
        disableCloseOnSelect
        clearIcon={null}
        popupIcon={null}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => {
          // Stay open while the edit submenu is showing; closing the input
          // would otherwise yank the dropdown out from behind the submenu.
          if (isEditOpen) return;
          setOpen(false);
        }}
        options={options}
        value={value}
        inputValue={inputValue}
        onInputChange={(_e, val) => setInputValue(val)}
        onChange={handleChange}
        filterOptions={filterOptions}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        disabled={disabled}
        renderValue={renderValue}
        renderOption={renderOption}
        renderInput={(params) => {
          // Map the Autocomplete params onto the non-deprecated `slotProps` API
          // (v7 removed top-level InputProps / inputProps / InputLabelProps).
          const { InputProps, inputProps, InputLabelProps, ...rest } = params;
          return (
            <TextField
              {...rest}
              label={label}
              placeholder={value.length === 0 ? placeholder || t('search') : ''}
              variant='outlined'
              size='small'
              slotProps={{
                input: InputProps,
                inputLabel: InputLabelProps,
                htmlInput: {
                  ...inputProps,
                  onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
                    inputProps.onFocus?.(e);
                    setFocused(true);
                  },
                  onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                    inputProps.onBlur?.(e);
                    setFocused(false);
                  },
                  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                    inputProps.onKeyDown?.(e);
                    if (
                      e.key === 'Enter' &&
                      !e.defaultPrevented &&
                      onCreateTag &&
                      inputValue.trim()
                    ) {
                      e.preventDefault();
                      handleCreateNew();
                    }
                  },
                },
              }}
            />
          );
        }}
        slotProps={{ paper: { sx: dropdownPaperSx, ref: setMenuEl } }}
      />

      <Popover
        open={isEditOpen}
        anchorReference='anchorPosition'
        anchorPosition={submenuAnchor}
        onClose={closeEdit}
        // Positioned beside the dropdown (8px gap), flipping to the left edge
        // when the right side is too close to the viewport.
        transformOrigin={{ vertical: 'top', horizontal: placeRight ? 'left' : 'right' }}
        slotProps={{ paper: { sx: S.editPaperSx } }}
      >
        <FormLabel sx={{ fontSize: '12px', fontWeight: 700 }}>{t('name')}</FormLabel>
        <TextField
          autoFocus
          value={edit.name}
          onChange={(e) => handleNameChange(e.target.value)}
          fullWidth
          variant='standard'
          slotProps={{
            input: {
              disableUnderline: true,
              sx: {
                border: '1px solid',
                borderColor: 'transparent',
                borderRadius: '6px',
                backgroundColor: 'grey.120',
                transition: 'background-color 0.2s, border-color 0.2s',
                '&.Mui-focused': { backgroundColor: 'common.white', borderColor: 'primary.main' },
              },
            },
            htmlInput: { sx: { fontSize: '12px' } },
          }}
        />

        {edit.error && (
          <Typography variant='caption' sx={{ color: 'error.main', fontSize: '10px', px: 0.5 }}>
            {edit.error}
          </Typography>
        )}

        <S.ColorSwatchRow>
          {TAG_COLORS.map((hex) => (
            <S.ColorSwatch
              key={hex}
              swatchColor={hex}
              selected={edit.color === hex}
              onClick={() => handleColorChange(hex)}
            />
          ))}
        </S.ColorSwatchRow>

        {onDeleteTag && (
          <>
            <Divider />
            <MenuItem
              aria-label='Delete tag'
              disabled={edit.isDeleting}
              onClick={() => void handleDelete()}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <Trash2 size={16} />
              </ListItemIcon>
              {t('delete')}
            </MenuItem>
          </>
        )}
      </Popover>
    </S.AutocompleteWrapper>
  );
}

/** Best-effort message extraction from an axios/Error rejection. */
function resolveError(err: unknown): string | null {
  if (typeof err === 'object' && err !== null) {
    const maybe = err as { response?: { data?: { message?: string } }; message?: string };
    return maybe.response?.data?.message || maybe.message || null;
  }
  return null;
}

export type { TagOption, TagSelectProps as TagSelectPropsType };
